import { parse, type SerializeOptions } from 'cookie';
import * as jose from 'jose';
import { authClient } from './auth.client';
import type { BugCategory, BugInsight } from '@/services/interfaceService';

export function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < length; i++) {
    result += charset[randomValues[i] % charset.length];
  }
  return result;
}

export async function getUserFromSession() {
  const { data: session } = await authClient.getSession();

  if (!session) {
    return null;
  }

  return session;
}

export async function generateCookieData(userId: string) {
  console.log('[JWT] === Starting JWT Generation ===');
  console.log('[JWT] User data:', userId);

  const secret = new TextEncoder().encode(process.env.JWT_SECRET || 'your-secret-key-change-in-production');

  console.log('[JWT] JWT Secret:', process.env.JWT_SECRET ? 'Present' : 'Using default');

  const jwt = await new jose.SignJWT({
    userId,
  })
    .setProtectedHeader({ alg: 'HS256' })
    .setIssuedAt()
    .setExpirationTime('90d')
    .sign(secret);

  console.log('[JWT] JWT generated successfully');
  console.log('[JWT] JWT length:', jwt.length);
  console.log('[JWT] === JWT Generation Completed ===');

  return { userId: jwt };
}

export async function verifyCookieData(token: string) {
  console.log('[JWT] === Starting JWT Verification ===');
  console.log('[JWT] Token length:', token.length);

  try {
    const secret = new TextEncoder().encode(process.env.JWT_SECRET || 'your-secret-key-change-in-production');

    console.log('[JWT] JWT Secret for verification:', process.env.JWT_SECRET ? 'Present' : 'Using default');

    const { payload } = await jose.jwtVerify(token, secret);
    console.log('[JWT] JWT verification successful');
    console.log('[JWT] Payload:', payload);
    console.log('[JWT] === JWT Verification Completed ===');

    return payload as { userId: string };
  } catch (error) {
    console.error('[JWT] === JWT Verification Failed ===');
    console.error('[JWT] Error type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('[JWT] Error message:', error instanceof Error ? error.message : error);
    console.error('[JWT] Error stack:', error instanceof Error ? error.stack : 'No stack trace');

    if (error instanceof Error) {
      console.error('[JWT] Error name:', error.name);
      console.error('[JWT] Error code:', (error as any).code);
    }

    return null;
  }
}

export const userCookieSession: SerializeOptions = {
  path: '/',
  httpOnly: true,
  sameSite: 'lax',
  secure: process.env.NODE_ENV === 'production',
  maxAge: 1000 * 60 * 60 * 24 * 90, // 90 days
};

export function parseLog(log: string): BugInsight {
  const errorTypes = new Set<string>();
  const messages: string[] = [];
  const files = new Set<string>();
  const components = new Set<string>();

  const normalized = log.toLowerCase();

  // Extract error types
  const errorTypeRegex = /(\w+Error)/g;
  let match;

  while ((match = errorTypeRegex.exec(log)) !== null) {
    errorTypes.add(match[1]);
  }

  // Extract messages
  const messageRegex = /Message:\s*(.*)/g;

  while ((match = messageRegex.exec(log)) !== null) {
    messages.push(match[1].trim());
  }

  // Extract file paths
  const fileRegex = /at\s+\w+\s+\((\/[^:]+):/g;

  while ((match = fileRegex.exec(log)) !== null) {
    files.add(match[1]);
  }

  // Extract components like <App>
  const componentRegex = /<([A-Z][A-Za-z0-9]*)>/g;

  while ((match = componentRegex.exec(log)) !== null) {
    components.add(match[1]);
  }

  return {
    errorTypes: [...errorTypes],
    messages,
    files: [...files],
    components: [...components],
    rawLog: log,
  };
}

export function classifyBugWithScores(insights: BugInsight) {
  const scores: Record<BugCategory, number> = {
    missing_import: 0,
    component_not_found: 0,
    routing_issue: 0,
    hook_misuse: 0,
    state_undefined: 0,
    react_error_boundary: 0,
    api_error: 0,
    syntax_error: 0,
    build_error: 0,
    unknown: 0,
  };

  const log = insights.rawLog.toLowerCase();
  const messages = insights.messages.map((m) => m.toLowerCase());

  const hasError = (type: string) => insights.errorTypes.some((e) => e.toLowerCase().includes(type.toLowerCase()));

  const includesAny = (arr: string[], keyword: string) => arr.some((item) => item.includes(keyword));

  // Missing import
  if (hasError('ReferenceError')) {
    scores.missing_import += 3;
  }

  if (includesAny(messages, 'is not defined')) {
    scores.missing_import += 4;
  }

  if (insights.components.length > 0) {
    scores.missing_import += 1;
  }

  // Component not found
  if (log.includes('element type is invalid')) {
    scores.component_not_found += 5;
  }

  if (log.includes('cannot read properties of undefined') && log.includes('render')) {
    scores.component_not_found += 3;
  }

  // Routing issues
  if (log.includes('no routes matched')) {
    scores.routing_issue += 4;
  }

  if (log.includes('cannot match route')) {
    scores.routing_issue += 4;
  }

  // Hook misuse
  if (log.includes('invalid hook call')) {
    scores.hook_misuse += 5;
  }

  if (log.includes('hooks can only be called')) {
    scores.hook_misuse += 5;
  }

  // State undefined
  if (hasError('TypeError')) {
    scores.state_undefined += 2;
  }

  if (log.includes('cannot read properties of undefined')) {
    scores.state_undefined += 4;
  }

  // React error boundary suggestion
  if (log.includes('consider adding an error boundary')) {
    scores.react_error_boundary += 4;
  }

  if (log.includes('logcapturederror')) {
    scores.react_error_boundary += 2;
  }

  // API errors
  if (log.includes('failed to fetch')) {
    scores.api_error += 4;
  }

  if (log.includes('axios')) {
    scores.api_error += 2;
  }

  // Syntax errors
  if (hasError('SyntaxError')) {
    scores.syntax_error += 5;
  }

  if (log.includes('unexpected token')) {
    scores.syntax_error += 4;
  }

  // Build errors (Vite/Webpack)
  if (log.includes('vite')) {
    scores.build_error += 2;
  }

  if (log.includes('failed to compile')) {
    scores.build_error += 4;
  }

  if (log.includes('module not found')) {
    scores.build_error += 4;
  }

  // Pick best category
  const sorted = Object.entries(scores).sort((a, b) => b[1] - a[1]);
  const [topCategory, topScore] = sorted[0];

  const totalScore = Object.values(scores).reduce((sum, val) => sum + val, 0);
  const confidence = totalScore > 0 ? topScore / totalScore : 0;

  return {
    category: topScore > 0 ? (topCategory as BugCategory) : 'unknown',
    confidence,
    scores,
    errorTypes: insights.errorTypes,
  };
}
